import { Component, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import * as L from 'leaflet';
import Swal from 'sweetalert2';

import { JobService } from '../services/job.service';
import { UserService } from '../../users/services/user.service';

import { Job } from '../models/job.model';
import { TaskLog } from '../models/task-log.model';
import { Worker } from '../../users/models/worker.model';
import { JobStatus } from '../models/job-status.enum';
import { environment } from 'src/app/environments/environment';

@Component({
  selector: 'app-job-details',
  templateUrl: './job-details.component.html',
  styleUrls: ['./job-details.component.css']
})
export class JobDetailsComponent implements OnInit, AfterViewInit, OnDestroy {

  jobId!: string;
  job?: Job;
  logs: TaskLog[]    = [];
  workers: Worker[]  = [];

  loading       = true;
  assigning     = false;
  assignSuccess = false;

  JobStatus         = JobStatus;
  selectedWorkerId  = '';

  // ✅ NEW — tells the template whether the map actually has enough
  // points to draw a connecting route line, vs. just a single marker.
  hasSinglePointOnly = false;

  get recentLogs(): TaskLog[] {
    return [...this.logs]
      .sort((a, b) => new Date(b.createdUtc).getTime() - new Date(a.createdUtc).getTime())
      .slice(0, 5);
  }

  get totalHours(): number {
    return this.logs.reduce((sum, l) => sum + (l.hoursSpent || 0), 0);
  }

  // ✅ NEW — used in the template for correct singular/plural wording
  // ("1 waypoint" vs "3 waypoints") instead of always saying "waypoints".
  get gpsPointCount(): number {
    return this.logs.filter(l => l.latitude != null && l.longitude != null).length;
  }

  private map!: L.Map;
  private polyline?: L.Polyline;
  private markers: L.CircleMarker[] = [];
  private mapReady = false;
  private routeSub?: Subscription;

  private avatarColors = [
    'linear-gradient(135deg, #1F3A5F, #2BB0A6)',
    'linear-gradient(135deg, #2BB0A6, #1F8F88)',
    'linear-gradient(135deg, #3498DB, #1F3A5F)',
    'linear-gradient(135deg, #F5A623, #E8821A)',
    'linear-gradient(135deg, #2ECC71, #1A9E4C)',
    'linear-gradient(135deg, #9B59B6, #6C3483)',
    'linear-gradient(135deg, #E74C3C, #C0392B)',
    'linear-gradient(135deg, #1ABC9C, #148F77)',
  ];

  constructor(
    private route: ActivatedRoute,
    private jobService: JobService,
    private userService: UserService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.loadWorkers();

    this.routeSub = this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (!id || id === this.jobId) return;

      this.jobId = id;
      this.loading = true;
      this.job = undefined;
      this.logs = [];

      this.clearMapLayers();

      this.loadJobData();
    });
  }

  ngAfterViewInit(): void {
    // Map initialization happens in loadJobData(), after loading is
    // actually false and the #jobMap div exists in the DOM.
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    if (this.map) {
      this.map.remove();
      (this as any).map = null;
    }
  }

  // ── Map ───────────────────────────────────────────────────

  initMap(): void {
    if (this.map) return;

    const el = document.getElementById('jobMap');
    if (!el) return;

    this.map = L.map('jobMap', { zoomControl: true }).setView([6.5244, 3.3792], 12);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19
    }).addTo(this.map);

    setTimeout(() => this.map?.invalidateSize({ animate: false }), 300);
    setTimeout(() => this.map?.invalidateSize({ animate: false }), 700);
    // ✅ NEW — an extra, later invalidateSize pass. Leaflet sizes itself
    // off the container's dimensions at the moment invalidateSize runs;
    // if surrounding cards/skeletons are still reflowing the page layout
    // slightly after 700ms (fonts loading, images loading, etc.), this
    // catches that and prevents a map that renders "blank" simply
    // because it calculated zero height/width on an earlier pass.
    setTimeout(() => this.map?.invalidateSize({ animate: false }), 1200);
  }

  private clearMapLayers(): void {
    if (this.polyline) {
      this.map?.removeLayer(this.polyline);
      this.polyline = undefined;
    }
    this.markers.forEach(m => this.map?.removeLayer(m));
    this.markers = [];
  }

  renderMap(): void {
    if (!this.map) return;

    this.clearMapLayers();

    const latLngs = this.logs
      .filter(l => l.latitude != null && l.longitude != null)
      .map(l => [l.latitude, l.longitude]) as L.LatLngExpression[];

    this.hasSinglePointOnly = latLngs.length === 1;

    if (latLngs.length === 0) return;

    // ✅ FIXED — markers are drawn for every point regardless of count,
    // so a job with only 1 logged point still shows a visible pin on the
    // map instead of an unexplained blank canvas.
    latLngs.forEach((ll, i) => {
      const isFirst = i === 0;
      const isLast  = i === latLngs.length - 1;
      const color   = isFirst ? '#2BB0A6' : isLast ? '#2ECC71' : '#1F3A5F';

      const marker = L.circleMarker(ll, {
        radius: isFirst || isLast ? 8 : 5,
        fillColor: color,
        color: '#fff',
        weight: 2,
        fillOpacity: 1
      }).addTo(this.map);

      this.markers.push(marker);
    });

    if (latLngs.length === 1) {
      // ✅ FIXED — a single point cannot form a "route" line. Previously
      // this still called L.polyline() with one coordinate (which draws
      // nothing) and then fitBounds() on a degenerate/zero-area bounds
      // object, which can behave unpredictably across Leaflet versions.
      // A single point now just centers the map on that marker directly.
      this.map.setView(latLngs[0], 15);
      return;
    }

    this.polyline = L.polyline(latLngs, {
      color: '#2BB0A6',
      weight: 3,
      opacity: 0.8,
      dashArray: '6 4'
    }).addTo(this.map);

    this.map.fitBounds(this.polyline.getBounds(), { padding: [24, 24] });
  }

  // ── Data ──────────────────────────────────────────────────

  loadWorkers(): void {
    this.userService.getWorkers().subscribe(w => (this.workers = w));
  }

  loadJobData(): void {
    this.jobService.getJobs().subscribe(jobs => {
      this.job = jobs.find(j => j.id === this.jobId);
    });

    this.jobService.getJobLogs(this.jobId).subscribe({
      next: logs => {
        this.logs    = logs;
        this.loading = false;

        setTimeout(() => {
          this.initMap();
          this.mapReady = true;
          this.renderMap();
        }, 50);
      },
      error: () => { this.loading = false; }
    });
  }

  loadJobLogs(): void {
    this.jobService.getJobLogs(this.jobId).subscribe({
      next: logs => {
        this.logs = logs;
        if (this.mapReady) this.renderMap();
      },
      error: () => { /* keep existing logs on refresh failure */ }
    });
  }

  // ── Actions ───────────────────────────────────────────────

  updateStatus(status: JobStatus): void {
    if (!this.job) return;
    this.jobService.updateJobStatus(this.job.id, { status }).subscribe(() => {
      this.job!.status = status;
    });
  }

  assignWorker(): void {
    if (!this.job || !this.selectedWorkerId) return;

    this.assigning = true;

    this.jobService.assignWorker(this.job.id, this.selectedWorkerId).subscribe({
      next: () => {
        this.job!.assignedUserId = this.selectedWorkerId;
        this.selectedWorkerId    = '';
        this.assigning           = false;
        this.assignSuccess       = true;
        setTimeout(() => (this.assignSuccess = false), 3000);
      },
      error: () => { this.assigning = false; }
    });
  }

  // ── Work log approval ────────────────────────────────────

  approveLog(logId: string): void {
    Swal.fire({
      title:             'Approve Work Log',
      input:             'textarea',
      inputLabel:        'Comment (optional)',
      inputPlaceholder:  'Add a note for the worker...',
      showCancelButton:  true,
      confirmButtonText: 'Approve',
      confirmButtonColor: '#2BB0A6',
      cancelButtonColor:  '#6B7280'
    }).then(result => {
      if (!result.isConfirmed) return;

      this.http.put(
        `${environment.apiUrl}/api/tasklogs/${logId}/approve`,
        { comment: result.value || '' }
      ).subscribe({
        next: () => {
          Swal.fire({
            title: 'Approved',
            text:  'Work log has been approved.',
            icon:  'success',
            confirmButtonColor: '#2BB0A6'
          });
          this.loadJobLogs();
        },
        error: (err) => {
          Swal.fire('Error',
            err?.error?.error ?? 'Could not approve log.', 'error');
        }
      });
    });
  }

  rejectLog(logId: string): void {
    Swal.fire({
      title:             'Reject Work Log',
      input:             'textarea',
      inputLabel:        'Reason for rejection *',
      inputPlaceholder:  'Explain clearly why this log is being rejected...',
      showCancelButton:  true,
      confirmButtonText: 'Reject',
      confirmButtonColor: '#E74C3C',
      cancelButtonColor:  '#6B7280',
      inputValidator:    (value) => {
        if (!value?.trim()) return 'Please provide a rejection reason.';
        return null;
      }
    }).then(result => {
      if (!result.isConfirmed) return;

      this.http.put(
        `${environment.apiUrl}/api/tasklogs/${logId}/reject`,
        { reason: result.value }
      ).subscribe({
        next: () => {
          Swal.fire({
            title: 'Rejected',
            text:  'Work log has been rejected.',
            icon:  'info',
            confirmButtonColor: '#2BB0A6'
          });
          this.loadJobLogs();
        },
        error: (err) => {
          Swal.fire('Error',
            err?.error?.error ?? 'Could not reject log.', 'error');
        }
      });
    });
  }

  // ── Helpers ───────────────────────────────────────────────

  getWorkerName(userId?: string | null): string {
    if (!userId) return 'Unassigned';
    return this.workers.find(w => w.id === userId)?.fullName ?? 'Unknown';
  }

  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : name.slice(0, 2).toUpperCase();
  }

  getAvatarColor(userId: string): string {
    const code = userId?.charCodeAt(0) ?? 0;
    return this.avatarColors[code % this.avatarColors.length];
  }

  getStatusLabel(status: JobStatus): string {
    const labels: Record<JobStatus, string> = {
      [JobStatus.Pending]:    'Pending',
      [JobStatus.InProgress]: 'In Progress',
      [JobStatus.Completed]:  'Completed',
      [JobStatus.Failed]:     'Failed',
    };
    return labels[status] ?? 'Unknown';
  }
}